/**
 * Login Page
 * Apple-inspired clean design with Google Sign-In
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import AppleSignin from 'react-apple-signin-auth';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, AlertCircle, Ship, Users, ArrowRight } from 'lucide-react';
import SEOHead from '../../components/SEOHead';
import { PAGE_SEO } from '../../utils/seoConstants';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Separate component for Google login to avoid hook issues when no client ID
function GoogleLoginButton({ onSuccess, onError, disabled }) {
  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess,
    onError: (errorResponse) => {
      console.error('Google OAuth error:', errorResponse);
      onError(errorResponse?.error_description || 'Google sign-in failed. Please try again.');
    },
    onNonOAuthError: (error) => {
      console.error('Google non-OAuth error:', error);
      if (error?.type === 'popup_closed') {
        onError('Google sign-in was cancelled.');
      } else {
        onError('Google sign-in could not load. Please check your browser settings and try again.');
      }
    },
  });

  return (
    <button
      type="button"
      onClick={() => googleLogin()}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-3 px-4 py-3.5 bg-white border border-navy-200 rounded-apple-lg hover:bg-navy-50 transition-all duration-300 disabled:opacity-50 mb-4"
    >
      <svg width="20" height="20" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      <span className="text-sm font-medium text-navy-700">Continue with Google</span>
    </button>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const { login, socialLogin, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialError, setSocialError] = useState('');

  const handleChange = (e) => {
    clearError();
    setSocialError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(formData);
      const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
      navigate(dashboardPath);
    } catch (err) {
      // Error is handled by AuthContext
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (tokenResponse) => {
    setLoading(true);
    setSocialError('');
    try {
      const data = await socialLogin('google', tokenResponse.access_token, {});

      if (data.needs_completion) {
        navigate('/register');
      } else {
        const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
        navigate(dashboardPath);
      }
    } catch (err) {
      setSocialError(err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apple auth config
  const appleAuthOptions = {
    clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
    scope: 'email name',
    redirectURI: window.location.origin + '/auth/apple/callback',
    usePopup: true,
  };

  const handleAppleSignIn = async (response) => {
    if (!response?.authorization) return;

    setLoading(true);
    setSocialError('');
    try {
      const idToken = response.authorization.id_token;
      const firstName = response.user?.name?.firstName || '';
      const lastName = response.user?.name?.lastName || '';

      const data = await socialLogin('apple', idToken, {
        first_name: firstName,
        last_name: lastName,
      });

      if (data.needs_completion) {
        navigate('/register');
      } else {
        const dashboardPath = data.user.user_type === 'helper' ? '/helper' : '/owner';
        navigate(dashboardPath);
      }
    } catch (err) {
      setSocialError(err?.message || 'Apple sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const displayError = error || socialError;

  return (
    <div>
      <SEOHead
        title={PAGE_SEO.login.title}
        description={PAGE_SEO.login.description}
        canonicalUrl="/login"
      />

      {/* Registration Cards */}
      <div className="mb-10">
        <h1 className="text-3xl font-bold text-navy-900 tracking-tight mb-2">
          Get Started
        </h1>
        <p className="text-navy-500 font-light mb-6">
          Join our community of yacht owners and helpers
        </p>

        {/* Google Sign-In */}
        {GOOGLE_CLIENT_ID && (
          <GoogleLoginButton
            onSuccess={handleGoogleSuccess}
            onError={setSocialError}
            disabled={loading}
          />
        )}

        {/* Apple Sign-In */}
        <AppleSignin
          authOptions={appleAuthOptions}
          onSuccess={handleAppleSignIn}
          onError={() => setSocialError('Apple sign-in failed')}
          noDefaultStyle
          render={(renderProps) => (
            <button
              onClick={renderProps.onClick}
              type="button"
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-3.5 bg-black rounded-apple-lg hover:bg-gray-900 transition-all duration-300 disabled:opacity-50 mt-3"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              <span className="text-sm font-medium text-white">Sign in with Apple</span>
            </button>
          )}
        />

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-navy-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-navy-400 font-light">or register with</span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3">
          <Link
            to="/register?type=owner"
            className="flex items-center p-4 bg-navy-900 text-white rounded-apple-lg transition-all duration-300 hover:bg-navy-800 group"
          >
            <div className="h-11 w-11 bg-white/10 rounded-apple flex items-center justify-center mr-4">
              <Ship className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <div className="text-base font-medium">Register as Captain / Owner</div>
              <div className="text-navy-400 text-sm font-light">I need crew or help mooring my yacht</div>
            </div>
            <ArrowRight className="h-5 w-5 text-navy-400 group-hover:translate-x-0.5 transition-transform duration-300" />
          </Link>
          <Link
            to="/register?type=helper"
            className="flex items-center p-4 bg-navy-100 text-navy-900 rounded-apple-lg transition-all duration-300 hover:bg-navy-200 group"
          >
            <div className="h-11 w-11 bg-navy-200 rounded-apple flex items-center justify-center mr-4">
              <Users className="h-5 w-5 text-navy-600" />
            </div>
            <div className="flex-1">
              <div className="text-base font-medium">Register as Helper / Crew</div>
              <div className="text-navy-500 text-sm font-light">I want to assist yacht owners</div>
            </div>
            <ArrowRight className="h-5 w-5 text-navy-400 group-hover:translate-x-0.5 transition-transform duration-300" />
          </Link>
        </div>
      </div>

      {/* Divider */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-navy-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-navy-400 font-light">Already have an account?</span>
        </div>
      </div>

      {/* Login Form */}
      <h2 className="text-xl font-semibold text-navy-900 mb-6 tracking-tight">
        Sign In
      </h2>

      {displayError && (
        <div className="mb-6 p-4 bg-red-50 rounded-apple flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
          <p className="text-red-700 text-sm font-light">{displayError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-normal text-navy-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="input-field"
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label htmlFor="password" className="block text-sm font-normal text-navy-700">
              Password
            </label>
            <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 transition-colors duration-300">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="input-field pr-12"
              placeholder="Enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-navy-400 hover:text-navy-600 transition-colors duration-300"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-3.5 text-base"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <span className="spinner h-5 w-5 mr-2" />
              Signing in...
            </span>
          ) : (
            'Sign In'
          )}
        </button>
      </form>
    </div>
  );
}

export default LoginPage;
